// Stefans-AI-CV-frontend/src/main.tsx

import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import './services/analytics'

// 环境变量检查
if (import.meta.env.DEV) {
  console.log("🚀 Stefans' AI CV")
  console.log('📊 Analytics:', import.meta.env.VITE_ENABLE_ANALYTICS)
  console.log('🔧 Debug Mode:', import.meta.env.VITE_DEBUG_MODE)
}

// 性能监控
if (import.meta.env.PROD) {
  // 生产环境性能监控
  const perfObserver = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.entryType === 'measure') {
        console.log(`⏱️ ${entry.name}: ${entry.duration.toFixed(2)}ms`)
      }
    }
  })

  perfObserver.observe({ entryTypes: ['measure'] })

  // 监控首次内容绘制
  new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.name === 'first-contentful-paint') {
        console.log(`🎨 First Contentful Paint: ${entry.startTime.toFixed(2)}ms`)
      }
    }
  }).observe({ entryTypes: ['paint'] })
}

// React 18 严格模式配置
const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error('Root element not found')
}

const root = ReactDOM.createRoot(rootElement)

// 错误边界组件
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error?: Error }> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🚨 React Error Boundary caught an error:', error, errorInfo)

    // 在生产环境中发送错误报告
    if (import.meta.env.PROD) {
      // 这里可以集成错误报告服务，如 Sentry
      console.error('Error reported to monitoring service')
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#020617',
            color: '#f8fafc',
            fontFamily: 'Inter, sans-serif',
            padding: '20px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              background: 'rgba(15, 23, 42, 0.8)',
              padding: '40px',
              borderRadius: '16px',
              border: '1px solid rgba(148, 163, 184, 0.1)',
              maxWidth: '500px',
              width: '100%',
            }}
          >
            <h1
              style={{
                fontSize: '24px',
                marginBottom: '16px',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              🚨 Oops! Something went wrong
            </h1>

            <p
              style={{
                color: '#94a3b8',
                marginBottom: '24px',
                lineHeight: '1.6',
              }}
            >
              The AI resume system encountered an unexpected error. Please refresh the page to try again.
            </p>

            <button
              onClick={() => window.location.reload()}
              style={{
                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                marginRight: '12px',
              }}
            >
              🔄 Refresh Page
            </button>

            <button
              onClick={() => window.history.back()}
              style={{
                background: 'rgba(148, 163, 184, 0.1)',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                borderRadius: '8px',
                padding: '12px 24px',
                color: '#f8fafc',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              ← Go Back
            </button>

            {import.meta.env.DEV && this.state.error && (
              <details
                style={{
                  marginTop: '24px',
                  textAlign: 'left',
                  background: 'rgba(239, 68, 68, 0.1)',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                }}
              >
                <summary
                  style={{
                    cursor: 'pointer',
                    color: '#fca5a5',
                    fontWeight: '500',
                    marginBottom: '8px',
                  }}
                >
                  🐛 Error Details (Dev Mode)
                </summary>
                <pre
                  style={{
                    fontSize: '12px',
                    color: '#fecaca',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.error.stack && '\n\nStack trace:\n' + this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// 渲染应用
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
)

// Web Vitals监控（仅在生产环境）
// if (import.meta.env.PROD && 'web-vitals' in window) {
//   import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
//     getCLS(console.log)
//     getFID(console.log)
//     getFCP(console.log)
//     getLCP(console.log)
//     getTTFB(console.log)
//   })
// }

// 服务工作者注册（如果需要PWA功能）
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('SW registered: ', registration)
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError)
      })
  })
}
